import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { TIDS } from "@/lib/testIds";
import { Save } from "lucide-react";

/**
 * Edit dialog for an employee. Admin-only — uses PATCH /api/admin/employees/:id.
 * Only sends changed fields; empty password means "don't change".
 *
 * Props:
 *  - employee: the current employee object (id, full_name, email, mobile)
 *  - open / onOpenChange: dialog state
 *  - onSaved(updatedEmployee): called after successful save
 */
export function EditEmployeeDialog({ employee, open, onOpenChange, onSaved }) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && employee) {
      setFullName(employee.full_name || "");
      setEmail(employee.email || "");
      setMobile(employee.mobile || "");
      setPassword("");
    }
  }, [open, employee]);

  const save = async (e) => {
    e?.preventDefault();
    if (!employee?.id) return;
    const payload = {};
    if (fullName.trim() !== (employee.full_name || "")) payload.full_name = fullName.trim();
    if (email.trim().toLowerCase() !== (employee.email || "").toLowerCase()) payload.email = email.trim();
    if ((mobile || "") !== (employee.mobile || "")) payload.mobile = mobile.trim();
    if (password.trim()) payload.password = password;

    if (Object.keys(payload).length === 0) {
      toast.message("Nothing to update");
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      const res = await apiClient.patch(`/admin/employees/${employee.id}`, payload);
      toast.success("Employee updated");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-employee-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-medium tracking-tight">Edit employee</DialogTitle>
          <DialogDescription>
            Update {employee?.full_name || "this employee"}&apos;s contact info or reset their password.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="emp-fn">Full name</Label>
            <Input
              id="emp-fn"
              data-testid={TIDS.editEmpName}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="emp-em">Email</Label>
            <Input
              id="emp-em"
              type="email"
              data-testid={TIDS.editEmpEmail}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="emp-mo">Mobile <span className="text-slate-400 font-normal text-xs">(for SMS login link)</span></Label>
            <Input
              id="emp-mo"
              type="tel"
              data-testid={TIDS.editEmpMobile}
              placeholder="+1 555 123 4567"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="emp-pw">
              Reset password <span className="text-slate-400 font-normal text-xs">(leave blank to keep current)</span>
            </Label>
            <Input
              id="emp-pw"
              type="text"
              data-testid={TIDS.editEmpPassword}
              placeholder="New password (min 8 chars)"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid={TIDS.editEmpSubmit}
              className="rounded-full bg-blue-600 hover:bg-blue-700"
            >
              <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
